/**
 * Multi-line address blocks ("next line" editing, Princewill
 * 2026-11-04): the bank address, account-holder address and payee
 * address are multi-line text - Enter starts the next line and the
 * check renders the lines stacked, like the write-up block on a real
 * bank header (his USAA example). The data model is unchanged: the
 * same checkData string fields, now allowed to contain \n. Canvas
 * text (fabric Text/IText) breaks lines natively; the helpers here
 * are the pure pieces the renderer uses, kept testable.
 */

/** Split a block value into its lines (CRLF-safe; '' -> ['']). */
export function splitLines(value: string | null | undefined): string[] {
    if (!value) return [''];
    return String(value).replace(/\r\n/g, '\n').split('\n');
}

/** How many rendered lines a block value occupies (minimum 1). */
export function lineCount(value: string | null | undefined): number {
    return splitLines(value).length;
}

/**
 * The bank block's address text: the address lines as written, with
 * the "Routing: NNN" line appended ONLY when a routing number
 * exists - a blank check must not print a dangling "Routing:" label
 * with nothing after it.
 */
export function bankBlockAddressText(bankAddress: string | null | undefined, routingNumber: string | null | undefined): string {
    const addr = (bankAddress || '').replace(/\r\n/g, '\n');
    const routing = (routingNumber || '').trim();
    if (!routing) return addr;
    return addr ? `${addr}\nRouting: ${routing}` : `Routing: ${routing}`;
}

export interface BankBlockShiftInput {
    /** Top of the bank header block (its layout/saved position). */
    bankTop: number;
    /** Top of the account-holder block it must not collide with. */
    holderTop: number;
    /** Lowest the holder block may be pushed (its bottom limit). */
    maxShift: number;
    /** The bank address value (lines counted from it). */
    bankAddress: string | null | undefined;
    /** Whether a routing number adds its line under the address. */
    hasRouting: boolean;
    /** Address font size on canvas (10 standard, 9 Karen). */
    addrFontSize: number;
}

/**
 * How far DOWN (px) the account-holder block must move so a
 * multi-line bank address never prints over it - real check headers
 * grow downward, and the holder block yields. The address text sits
 * 18px into the bank block (under the bank name) at the fabric
 * default 1.16 line height. Returns 0 when the block fits, and is
 * clamped at maxShift: beyond that the header is taller than the
 * zone can absorb (the blocks stay draggable so the user can make
 * room by hand).
 */
export function bankBlockShiftPx(input: BankBlockShiftInput): number {
    const lines = lineCount(input.bankAddress) + (input.hasRouting ? 1 : 0);
    const bankBottom = input.bankTop + 18 + lines * input.addrFontSize * 1.16;
    const overflow = bankBottom - input.holderTop + 2; // 2px breathing room
    if (overflow <= 0) return 0;
    return Math.min(overflow, Math.max(0, input.maxShift));
}
